import React, { useRef, useMemo } from 'react';
import {
  Editor,
  EditorState,
  SelectionState,
  RichUtils,
  getDefaultKeyBinding,
  Modifier,
} from 'draft-js';
import './prism.css';
import './Textarea.css';
import { LineNumber } from './components';
import { Label, Icon } from '../';
import { ValidationMessage } from '../ValidationMessage';
import { useModalDialog, useTooltip } from '../../hooks';
import { textareaCodeInitialState, editorStateToPlainText } from './utils';

import { TAB_KEY_CODE } from './helpers';
import { getPrismDecorator } from './decorators';

export const TextareaCode = ({
  name,
  value,
  label = undefined,
  title = undefined,
  onChange,
  placeholder,
  readOnly = false,
  disabled = false,
  showLineNumbers = true,
  isOptional = false,
  language = 'sql',
  validation = undefined,
  expandable = false,
}) => {
  const editorRef = useRef();
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { showTooltip, hideTooltip } = useTooltip();

  /**
   * Hands the value to the query modal, which is the same editor with room to work in, and takes
   * the edited text back. Stops the click here: the wrapper focuses the editor on click, which
   * would otherwise happen behind the dialog.
   */
  const handleExpand = event => {
    event.stopPropagation();

    showModalDialog(
      'editCode',
      {
        // Label also accepts `true`/`false` to drive its displayNames fallback, so only a
        // plain string is a usable title; the dialog names itself when there is none.
        title: typeof label === 'string' ? label : undefined,
        value: editorStateToPlainText(value),
        language,
        readOnly,
      },
      text => {
        onChange && onChange({ name, value: textareaCodeInitialState({ value: text }) });
        hideModalDialog();
      }
    );
  };

  const decoratedValue = useMemo(() => {
    const targetDecorator = getPrismDecorator(language);
    if (value.getDecorator() === targetDecorator) return value;
    return EditorState.set(value, { decorator: targetDecorator });
  }, [value, language]);

  const blockRendererFn = () => ({
    component: LineNumber,
    data: { syntax: language },
  });

  const myKeyBindingFn = e => {
    switch (e.keyCode) {
      case TAB_KEY_CODE:
        if (e.getModifierState('Shift')) {
          return 'shift-tab';
        } else {
          return 'tab';
        }
        break;
      default:
        break;
    }

    return getDefaultKeyBinding(e);
  };

  const handleChange = val => {
    onChange && onChange({ name: name, value: val });
  };

  const handleKeyCommand = command => {
    let newEditorState;
    let newContentState = RichUtils.handleKeyCommand(value, command);

    const currentSelection = value.getSelection();
    const currentContent = value.getCurrentContent();

    switch (command) {
      case 'tab':
        const isRangeSelection =
          currentSelection.getAnchorKey() !== currentSelection.getFocusKey() ||
          currentSelection.getFocusOffset() - currentSelection.getAnchorOffset() !== 0;

        // No range selected -> insert tab at cursor position
        if (!isRangeSelection) {
          const newSelection = new SelectionState({
            anchorKey: value.getSelection().getAnchorKey(),
            focusKey: value.getSelection().getFocusKey(),
            anchorOffset: value.getSelection().getAnchorOffset(),
            focusOffset: value.getSelection().getAnchorOffset(),
          });

          newContentState = Modifier.insertText(value.getCurrentContent(), newSelection, `\t`);

          const newEditorState = EditorState.push(value, newContentState, 'insert-characters');

          onChange &&
            onChange({
              name: name,
              value: EditorState.forceSelection(
                newEditorState,
                newContentState.getSelectionAfter()
              ),
            });

          // Range selected -> insert tab at start of line for each line in selection
        } else {
          const startKey = currentSelection.getStartKey();
          const endKey = currentSelection.getEndKey();

          const currentContent = value.getCurrentContent();

          const blockList = currentContent.getBlockMap().toList();

          const startIndex = blockList.findIndex(item => item.key === startKey);
          const endIndex = blockList.findIndex(item => item.key === endKey);

          newContentState = currentContent;

          // Add a tab to each block in selection
          for (let i = startIndex; i <= endIndex; i++) {
            const currentBlock = blockList.get(i);

            const newSelection = new SelectionState({
              anchorKey: currentBlock.getKey(),
              focusKey: currentBlock.getKey(),
              anchorOffset: 0,
              focusOffset: 0,
            });

            newContentState = Modifier.insertText(newContentState, newSelection, `\t`);
          }

          const newSelection = new SelectionState({
            anchorKey: currentSelection.getAnchorKey(),
            focusKey: currentSelection.getFocusKey(),
            anchorOffset: currentSelection.getAnchorOffset() + 1,
            focusOffset: currentSelection.getFocusOffset() + 1,
            isBackward: currentSelection.getIsBackward(),
          });

          const newEditorState = EditorState.push(value, newContentState, 'insert-characters');

          onChange &&
            onChange({
              name: name,
              value: EditorState.forceSelection(newEditorState, newSelection),
            });
        }

        break;

      // Delete tab or whitespace at the beginning of each row
      case 'shift-tab':
        const startKey = currentSelection.getStartKey();
        const endKey = currentSelection.getEndKey();

        const blockList = currentContent.getBlockMap().toList();

        const startIndex = blockList.findIndex(item => item.key === startKey);
        const endIndex = blockList.findIndex(item => item.key === endKey);

        newContentState = currentContent;

        let removedFromFirst = false;
        let removedFromLast = false;

        // Delete tab or whitespace from beginning of each block in selection
        for (let i = startIndex; i <= endIndex; i++) {
          const currentBlock = blockList.get(i);
          const firstCharacter = currentBlock.getText()[0];
          const characterList = [`\t`, ' '];

          if (characterList.includes(firstCharacter)) {
            const newSelection = new SelectionState({
              anchorKey: currentBlock.getKey(),
              focusKey: currentBlock.getKey(),
              anchorOffset: 0,
              focusOffset: 1,
            });

            newContentState = Modifier.replaceText(newContentState, newSelection, ``);

            if (i === startIndex) removedFromFirst = true;
            if (i === endIndex) removedFromLast = true;
          }
        }

        const currentAnchorOffset = currentSelection.getAnchorOffset();
        const currentFocusOffset = currentSelection.getFocusOffset();

        const newAnchorOffset = removedFromFirst
          ? currentAnchorOffset > 0
            ? currentAnchorOffset - 1
            : 0
          : currentSelection.getAnchorOffset();
        const newFocusOffset = removedFromLast
          ? currentFocusOffset > 0
            ? currentFocusOffset - 1
            : 0
          : currentSelection.getFocusOffset();

        const newSelection = new SelectionState({
          anchorKey: currentSelection.getAnchorKey(),
          focusKey: currentSelection.getFocusKey(),
          anchorOffset: newAnchorOffset,
          focusOffset: newFocusOffset,
          isBackward: currentSelection.getIsBackward(),
        });

        const newEditorState = EditorState.push(value, newContentState, 'remove-characters');

        onChange &&
          onChange({
            name: name,
            value: EditorState.forceSelection(newEditorState, newSelection),
          });
        break;

      // Select all
      case 'command-a':
        newContentState = value.getSelection().merge({
          anchorKey: currentContent.getFirstBlock().getKey(),
          anchorOffset: 0,

          focusOffset: currentContent.getLastBlock().getText().length,
          focusKey: currentContent.getLastBlock().getKey(),
        });
        break;
      default:
        break;
    }

    if (newContentState) {
      return 'handled';
    }
    return 'not-handled';
  };

  const handleFocus = state => {};

  return (
    <div
      className={
        'CustomTextareaCode' +
        (readOnly ? ' read-only' : '') +
        (disabled ? ' disabled' : '') +
        (showLineNumbers ? ' show-line-numbers' : '') +
        (validation && !validation.valid ? ' has-error' : '')
      }
      onClick={() => editorRef.current.focus()}
    >
      <div className="custom-textarea-content">
        <div className="custom-textarea-label-row">
          {/* The language is the label's suffix rather than part of `label` itself: a node there is
              always truthy, which defeats Label's own "nothing to show" guard and its displayNames
              fallback for the callers that pass no label. It is shown only where the caller gives
              one — the technical definition views pass no label and put the language in their
              toolbar instead. */}
          <Label
            label={label}
            suffix={
              label &&
              language && <span className="custom-textarea-label-language">({language})</span>
            }
            htmlFor={name}
            title={title}
            disabled={disabled}
            isOptional={isOptional}
          />
          {/* Beside the label rather than inside it: a <label> may hold one labelable control, and
              anything clickable in there would focus the editor as well as act. */}
          {expandable && !disabled && (
            <div
              className="custom-textarea-label-button"
              onMouseEnter={() => showTooltip({ title: 'Open in separate window' })}
              onMouseLeave={() => hideTooltip()}
            >
              <Icon
                name="expand"
                className="custom-textarea-expand"
                tooltip={readOnly ? 'Open in a larger window' : 'Edit in a larger window'}
                onClick={handleExpand}
              />
            </div>
          )}
        </div>
        <Editor
          ref={editorRef}
          editorState={decoratedValue}
          onChange={handleChange}
          keyBindingFn={myKeyBindingFn}
          handleKeyCommand={handleKeyCommand}
          blockRendererFn={blockRendererFn}
          stripPastedStyles={true}
          placeholder={placeholder}
          readOnly={readOnly || disabled}
          onFocus={handleFocus}
          onBlur={e => e.preventDefault()}
        />
      </div>
      <ValidationMessage validation={validation} />
    </div>
  );
};
